import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { computed, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, Observable, of, ReplaySubject, tap, throwError } from 'rxjs';
import { ApiRouter } from '../../../../../global/routes/api-routes';
import { getBrandImagesForManufacturer } from '../../../../../shared/utils/get-model-number-image';
import { ClaimType } from '../../../../mmr/mmr.types';
import { ClaimFormDataResponse } from '../../../types/claim-form.types';
import { VisitAppointmentsData } from '../../../types/visit-appointments.types';
import { ClaimFormStep } from '../claim-form.types';

@Injectable({
  providedIn: 'root',
})
export class ClaimsFormService {
  planNumber = signal<string | undefined>(undefined);
  claimId!: string;
  formValue: Partial<Record<ClaimFormStep, Record<string, any>>> = {};
  initialFormValue = new ReplaySubject<Record<string, any>>(1);

  // will only get set once the claim has initially been resumed
  readonly claimResumed = signal(false);

  readonly isLoading = signal(false);
  readonly formSubmitting = signal(false);
  readonly currentClaimState = signal<ClaimFormDataResponse | undefined>(undefined);

  currentSubStep = signal(0);
  private claimState = computed(() => {
    const claimStep = this.currentClaimState()?.steps?.[0];
    const activeStep: any | undefined = claimStep?.stepConfig?.[claimStep.steps?.[this.currentSubStep()]];

    const stepConfig = claimStep?.stepConfig;

    const item = this.currentClaimState()?.state?.item;
    const contactDetails = this.currentClaimState()?.state?.contact;

    const brandImages = getBrandImagesForManufacturer(item?.code, item?.description, item?.manufacturer?.name);
    const jumps =
      (this.currentClaimState()?.jumps ?? [])
        ?.filter((i) => i.direction !== 'ORTHOGONAL')
        ?.sort((a, b) => a.priority - b.priority) ?? [];

    const currentStepPosition =
      jumps.findIndex((i) => i.direction === 'FORWARDS') >= 0
        ? jumps.findIndex((i) => i.direction === 'FORWARDS')
        : jumps.length;

    // Add the active step in for display purposes
    jumps.splice(currentStepPosition, 0, {
      direction: 'ACTIVE',
      to: {
        step: this.currentClaimState()?.state.currentStep,
      },
    } as any);

    return {
      claimId: this.currentClaimState()?.executionId,
      planNumber: this.planNumber() as string,
      isLoading: this.isLoading(),
      submittingValue: this.formSubmitting(),
      claimResumed: this.claimResumed(),
      currentSubStep: this.currentSubStep(),
      currentClaim: this.currentClaimState(),
      currentClaimSteps: claimStep,
      activeStep,
      brandImages,
      stepConfig,
      contactDetails,
      jumps,
      maximumAvailabilityLookup:
        this.currentClaimState()?.state?.additionalData?.serviceOption?.serviceProviders?.[0]?.availabilityDays,
    };
  });

  get state() {
    return this.claimState();
  }

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router
  ) {}

  reset(): void {
    this.currentClaimState.set(undefined);
    this.currentSubStep.set(0);
    this.formSubmitting.set(false);
    this.claimResumed.set(false);
    this.formValue = {};
    this.initialFormValue.next(this.formValue);
  }

  goToPreviousStep(): void {
    if (this.state.currentSubStep === 0) {
      const sortedJumps = this.state.currentClaim!.jumps.sort((a: any, b: any) => b.priority - a.priority);
      const backwardsJump =
        sortedJumps[sortedJumps.findIndex((i: any) => i.direction === 'BACKWARDS' && i.resumableFromCurrent)];

      if (!backwardsJump) {
        // Nowhere to go, go back to MMR page!
        this.router.navigate(['/', this.state.planNumber, 'claims']);
        return;
      }

      this.resumeClaim(this.state.claimId!, backwardsJump.to.step, undefined, true);

      return;
    }

    this.currentSubStep.set(this.state.currentSubStep - 1);
  }

  goToNextStep(): Observable<any> {
    const stepsWithOnlyOnePart = ['contact_details', 'initial_visit_appointment'];
    if (
      (this.state.currentClaimSteps?.steps ?? []).length - 1 === this.currentSubStep() ||
      stepsWithOnlyOnePart.includes(this.state.currentClaim?.state.currentStep as string)
    ) {
      const currentStepId = this.state.currentClaimSteps?.id ?? this.state.currentClaim?.state.currentStep;
      const url = ApiRouter.submitStep(this.state.planNumber, this.state.claimId!, currentStepId!);

      this.formSubmitting.set(true);
      const currentFormStepData = this.formValue[this.state.currentClaim?.state.currentStep as ClaimFormStep];
      return this.http
        .post<ClaimFormDataResponse>(url, {
          substep: currentStepId,
          data: currentFormStepData,
        })
        .pipe(
          tap((value: ClaimFormDataResponse) => {
            this.setFormValues(value);
            this.formSubmitting.set(false);
            this.currentSubStep.set(0);
            if (value.state.currentStep === 'visit_booked') {
              this.router.navigate(['/', this.state.planNumber, 'confirmation']);
              sessionStorage.setItem('confirmedBooking', JSON.stringify(value));

              return;
            }
            this.navigateToStep(value.state.currentStep);
            this.currentClaimState.set(value);
          })
        );
    }

    this.currentSubStep.set(this.currentSubStep() + 1);

    return of({});
  }

  navigateToStep(step: string): void {
    if (!this.state.planNumber) return;
    this.router.navigate(['/', this.state.planNumber, 'book-claim', step]);
  }

  setPlanNumber(planNumber: string) {
    if (!planNumber) return;
    this.claimResumed.set(false);
    this.planNumber.set(planNumber);
  }

  storeFormValue(stepName: ClaimFormStep, value: any): void {
    this.formValue = {
      ...this.formValue,
      [stepName]: {
        ...(this.formValue[stepName] || {}),
        ...value,
      },
    };

    this.initialFormValue.next(this.formValue);
  }

  resumeClaim(
    claimId: string,
    currentStep?: string,
    planNumber?: string,
    goToLastStep: boolean = false,
    errorCallback?: () => void
  ): void {
    if (this.claimResumed() && !currentStep) return;

    if (planNumber) {
      this.setPlanNumber(planNumber);
    }

    this.claimResumed.set(true);
    this.isLoading.set(true);
    // this.navigateToStep(currentStep);
    this.http
      .post<ClaimFormDataResponse>(ApiRouter.resumeClaim(this.state.planNumber), {
        claimId,
        currentStep,
      })
      .subscribe({
        next: (val) => {
          this.setFormValues(val);
          this.setPlanNumber(val.state.planReference);
          this.navigateToStep(val.state.currentStep);

          if (goToLastStep) {
            this.currentSubStep.set(val.steps[0].steps.length - 1);
          }

          this.isLoading.set(false);
          this.currentClaimState.set(val);
        },
        error: () => {
          this.isLoading.set(false);
          errorCallback?.();
        },
      });
  }

  startClaim(claimType: ClaimType, fromMMRPage: boolean = false): Observable<ClaimFormDataResponse> {
    this.formSubmitting.set(true);

    // Show full page loading in form view
    if (fromMMRPage) {
      this.isLoading.set(true);
      this.navigateToStep('start-claim');
    }

    return this.http.post<ClaimFormDataResponse>(ApiRouter.startClaim(this.state.planNumber), claimType).pipe(
      tap((val) => {
        this.setFormValues(val);
        this.formSubmitting.set(false);
        this.setPlanNumber(val.state.planReference);
        this.navigateToStep(val.state.currentStep);
        this.isLoading.set(false);
        this.currentClaimState.set(val);
      }),
      catchError((error: HttpErrorResponse) => {
        console.error(error);
        this.isLoading.set(false);
        this.formSubmitting.set(false);

        return throwError(() => error);
      })
    );
  }

  /**
   * Pull out initial values from returned data structure to populate the form value
   */
  setFormValues(value: ClaimFormDataResponse): void {
    value.steps.forEach((step) => {
      if (!this.formValue[step.id as ClaimFormStep]) {
        this.formValue[step.id as ClaimFormStep] = {};
      }

      Object.values(step.stepConfig).forEach((subStep) => {
        this.formValue[step.id as ClaimFormStep]![subStep.name] = subStep.value;
      });
    });

    this.initialFormValue.next(this.formValue);
  }

  getVisitAppointments(): Observable<VisitAppointmentsData> {
    return of({} as VisitAppointmentsData);
    // const { planNumber, claimId, maximumAvailabilityLookup } = this.state;
    // return this.http.post<VisitAppointmentsData>(ApiRouter.visitAppointments(planNumber, claimId!), {
    //   maximumAvailabilityDates: parseInt(`${maximumAvailabilityLookup}`),
    // });
  }
}
